import React, { useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../../context/Web3Context";
import { useAdminTx } from "../../hooks/useContract";
import { useNotification } from "../../hooks/useNotification";
import { formatUSDT, parseUSDT, shortAddress } from "../../utils/format";
import Button from "../../components/Common/Button";
import { Card } from "../../components/Common/Card";
import { CONTRACT_ADDRESS, USDT_ADDRESS } from "../../config/contract";

// OWNER-ONLY component
function AdminSystem() {
  const notify = useNotification();
  const { account, getContract, getUsdt } = useWeb3();
  const { send, pending } = useAdminTx();

  const [state, setState] = useState({
    owner: "",
    admin: "",
    totalUsers: "0",
    nextSerial: "0",
    totalInvestment: ethers.BigNumber.from(0),
    contractUsdtBalance: ethers.BigNumber.from(0),
  });
  const [reloadKey, setReloadKey] = useState(0);
  const cancelRef = useRef(false);

  const [newAdmin, setNewAdmin] = useState("");
  const [newOwner, setNewOwner] = useState("");
  const [withdrawAmt, setWithdrawAmt] = useState("");

  useEffect(() => {
    cancelRef.current = false;
    const load = async () => {
      const contract = getContract();
      const usdt = getUsdt();
      if (!contract) return;
      try {
        const [owner, admin, total, next, totalInv] = await Promise.all([
          contract.owner(),
          contract.admin(),
          contract.totalUsers(),
          contract.nextSerialNumber(),
          contract.totalInvestment().catch(() => ethers.BigNumber.from(0)),
        ]);
        let bal = ethers.BigNumber.from(0);
        if (usdt) {
          try {
            bal = await usdt.balanceOf(CONTRACT_ADDRESS);
          } catch {}
        }
        if (!cancelRef.current) {
          setState({
            owner,
            admin,
            totalUsers: total.toString(),
            nextSerial: next.toString(),
            totalInvestment: totalInv,
            contractUsdtBalance: bal,
          });
        }
      } catch {
        // ignore
      }
    };
    load();
    return () => {
      cancelRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const doUpdateAdmin = async () => {
    if (!ethers.utils.isAddress(newAdmin)) return notify.error("Invalid address");
    const contract = getContract();
    if (!contract) return;
    const ok = await send(
      () => contract.updateAdmin(newAdmin),
      {
        pendingMsg: "Updating admin...",
        successMsg: "Admin updated",
        action: "update_admin_address",
        params: { newAdmin },
      }
    );
    if (ok) {
      setNewAdmin("");
      reload();
    }
  };

  const doTransferOwnership = async () => {
    if (!ethers.utils.isAddress(newOwner)) return notify.error("Invalid address");
    if (!window.confirm("Transfer ownership? You will lose owner access.")) return;
    const contract = getContract();
    if (!contract) return;
    const ok = await send(
      () => contract.transferOwnership(newOwner),
      {
        pendingMsg: "Transferring ownership...",
        successMsg: "Ownership transferred",
        action: "transfer_ownership",
        params: { newOwner },
      }
    );
    if (ok) {
      setNewOwner("");
      reload();
    }
  };

  const doEmergencyWithdraw = async () => {
    if (!withdrawAmt || Number(withdrawAmt) <= 0) return notify.error("Enter amount");
    if (!window.confirm(`Withdraw ${withdrawAmt} USDT to your wallet?`)) return;
    const contract = getContract();
    if (!contract) return;
    const ok = await send(
      () => contract.emergencyWithdraw(parseUSDT(withdrawAmt)),
      {
        pendingMsg: "Withdrawing...",
        successMsg: "Withdrawn",
        action: "emergency_withdraw",
        params: { amount: String(withdrawAmt) },
      }
    );
    if (ok) {
      setWithdrawAmt("");
      reload();
    }
  };

  return (
    <div>
      {/* Contract stats */}
      <div className="section-header">
        <div className="section-title">Contract Overview</div>
      </div>
      <div className="stat-grid mb-md">
        <Card>
          <div className="stat-card-label">Total Users</div>
          <div className="stat-card-value" style={{ color: "var(--cyan)" }}>{state.totalUsers}</div>
        </Card>
        <Card>
          <div className="stat-card-label">Next Serial</div>
          <div className="stat-card-value">{state.nextSerial}</div>
        </Card>
      </div>

      <Card className="mb-md">
        <div className="stat-card-label">Total Investment</div>
        <div style={{ fontFamily: "'Sora', 'Inter', sans-serif", fontSize: 28, fontWeight: 700, color: "var(--success)", letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
          ${formatUSDT(state.totalInvestment)}
        </div>
        <div className="stat-card-sub">Cumulative USDT collected</div>
      </Card>

      <Card className="mb-lg" glow>
        <div className="stat-card-label">Contract USDT Balance</div>
        <div style={{ fontFamily: "'Sora', 'Inter', sans-serif", fontSize: 32, fontWeight: 700, color: "var(--success)", letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
          ${formatUSDT(state.contractUsdtBalance)}
        </div>
        <div className="stat-card-sub">Live balance on contract</div>
      </Card>

      <Card className="mb-lg">
        <div className="stat-card-label">Contract</div>
        <div className="mono text-cyan mb-sm" style={{ fontSize: 12, wordBreak: "break-all" }}>
          {CONTRACT_ADDRESS}
        </div>
        <div className="stat-card-label mt-md">USDT</div>
        <div className="mono text-cyan" style={{ fontSize: 12, wordBreak: "break-all" }}>
          {USDT_ADDRESS}
        </div>
      </Card>

      {/* Roles */}
      <div className="section-header">
        <div className="section-title">Roles</div>
      </div>
      <Card className="mb-lg">
        <div className="stat-card-label">Owner</div>
        <div className="mono text-cyan mb-md" style={{ fontSize: 12, wordBreak: "break-all" }}>
          {state.owner}
        </div>
        <div className="stat-card-label">Admin</div>
        <div className="mono text-cyan" style={{ fontSize: 12, wordBreak: "break-all" }}>
          {state.admin}
        </div>
      </Card>

      {/* Update admin */}
      <div className="section-header">
        <div className="section-title">Change Admin</div>
      </div>
      <Card className="mb-lg">
        <div className="input-group">
          <label className="input-label">New Admin Address</label>
          <input
            className="input"
            placeholder="0x..."
            value={newAdmin}
            onChange={(e) => setNewAdmin(e.target.value)}
          />
        </div>
        <Button variant="primary" full loading={pending} onClick={doUpdateAdmin}>
          Update Admin
        </Button>
      </Card>

      {/* Transfer ownership */}
      <div className="section-header">
        <div className="section-title">Transfer Ownership</div>
      </div>
      <Card className="mb-lg">
        <div className="network-warning" style={{ marginBottom: 12 }}>
          ⚠ This action cannot be undone.
        </div>
        <div className="input-group">
          <label className="input-label">New Owner Address</label>
          <input
            className="input"
            placeholder="0x..."
            value={newOwner}
            onChange={(e) => setNewOwner(e.target.value)}
          />
        </div>
        <Button variant="ghost" full loading={pending} onClick={doTransferOwnership}>
          Transfer Ownership
        </Button>
      </Card>

      {/* Emergency withdraw */}
      <div className="section-header">
        <div className="section-title">Emergency Withdraw</div>
      </div>
      <Card className="mb-lg">
        <p className="text-muted mb-md" style={{ fontSize: 12 }}>
          Withdraw USDT from contract to your wallet.
        </p>
        <div className="input-group">
          <label className="input-label">Amount (USDT)</label>
          <input
            type="number"
            className="input"
            value={withdrawAmt}
            onChange={(e) => setWithdrawAmt(e.target.value)}
            placeholder="0.00"
          />
        </div>
        <Button variant="ghost" full loading={pending} onClick={doEmergencyWithdraw}>
          🚨 Withdraw
        </Button>
      </Card>
    </div>
  );
}

export default AdminSystem;
